import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppBranding } from '../../../../../config/branding';
import { DeviceDetailsProperties } from '../../../../../interface/device-management.interface';
import { useAppDispatch } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import ConnectorSidePanel from '../../../../../shared-components/table-side-panel/ConnectorSidePanel';
import StationSidePanel from '../../../../../shared-components/table-side-panel/LocationSidePanel';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import TabsContainer from '../../../../../shared-components/tabs-container/TabsContainer';
import chargingLocationAPI from '../../../../../utils/api/charging-location';
import deviceApi from '../../../../../utils/api/device';
import deviceHubApi from '../../../../../utils/api/device-hub';
import devicePartnerApi from '../../../../../utils/api/partner/device-partner';
import stationPartnerApi from '../../../../../utils/api/partner/station';

import DeviceMap from './DeviceMap';

interface DeviceConnectorProperties {
  selectedDeviceGeneral: DeviceDetailsProperties;
  setSelectedDeviceGeneral?: React.Dispatch<React.SetStateAction<DeviceDetailsProperties>>;
}

interface AvailableTabProperties {
  label: string | React.ReactNode;
  key: string;
  isActive: boolean;
}

/**
 * Extract filename from a URL
 * @param {string} url - The URL string
 * @returns {string} The filename
 */
const getFileNameFromUrl = (url: string) => {
  if (!url) return '';
  const decoded = decodeURIComponent(url);
  const parts = decoded.split('/');
  const lastPart = parts[parts.length - 1];
  return lastPart.split('?')[0];
};

/**
 * Device Connector
 * @param {any} properties - Component properties
 * @param {any} properties.selectedDeviceGeneral - The selectedDeviceGeneral prop
 * @param {any} properties.setSelectedDeviceGeneral - The setSelectedDeviceGeneral prop
 * @returns {any} Device connector component
 */
function DeviceConnector({
  selectedDeviceGeneral,
  setSelectedDeviceGeneral,
}: DeviceConnectorProperties) {
  const { t, i18n } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const { images: brandImage } = useAppBranding();
  const location = useLocation();
  const hasDeviceHub = location.pathname.includes('ev-device-hub');
  const isChargerManagement =
    location.pathname.includes('charger-management') ||
    location.pathname.includes('charger-expanded') ||
    location.pathname.includes('operations/chargers');
  const isDeviceLinked =
    (selectedDeviceGeneral as any)?.deviceOwner &&
    (selectedDeviceGeneral as any)?.deviceOwner !== '-';
  const showConnectorActions = hasDeviceHub && !isDeviceLinked;
  const connectors = selectedDeviceGeneral?.connectors || [];
  const currentType = (selectedDeviceGeneral as any)?.currentType || '-';
  const [fetchedStationData, setFetchedStationData] = useState<any>(null);

  const stationId =
    typeof (selectedDeviceGeneral as any).chargingStation === 'object'
      ? (selectedDeviceGeneral as any).chargingStation?._id ||
        (selectedDeviceGeneral as any).chargingStation?.id
      : (selectedDeviceGeneral as any).chargingStation ||
        (selectedDeviceGeneral as any).chargingStationId;

  const stationName =
    selectedDeviceGeneral?.stationName ||
    (selectedDeviceGeneral as any)?.chargingStationName ||
    (selectedDeviceGeneral as any)?.chargingStation?.name ||
    (selectedDeviceGeneral as any)?.chargingStation?.stationName ||
    (selectedDeviceGeneral as any)?.chargingStation?.chargingStationName ||
    (selectedDeviceGeneral as any)?.chargingStation?.title ||
    fetchedStationData?.name ||
    fetchedStationData?.stationName ||
    fetchedStationData?.chargingStationName ||
    '';

  const partnerName =
    (selectedDeviceGeneral as any)?.partnerName ||
    (selectedDeviceGeneral as any)?.accountName ||
    (selectedDeviceGeneral as any)?.account?.accountName ||
    (selectedDeviceGeneral as any)?.account?.name ||
    (selectedDeviceGeneral as any)?.partner?.partnerName ||
    (selectedDeviceGeneral as any)?.partner?.name ||
    (selectedDeviceGeneral as any)?.chargingStation?.accountName ||
    (selectedDeviceGeneral as any)?.chargingStation?.account?.accountName ||
    (selectedDeviceGeneral as any)?.chargingStation?.account?.name ||
    (selectedDeviceGeneral as any)?.chargingStation?.partnerName ||
    (selectedDeviceGeneral as any)?.chargingStation?.partner?.name ||
    (selectedDeviceGeneral as any)?.cpoName ||
    (selectedDeviceGeneral as any)?.tenantName ||
    (selectedDeviceGeneral as any)?.tenantId ||
    fetchedStationData?.accountName ||
    fetchedStationData?.account?.accountName ||
    fetchedStationData?.account?.name ||
    fetchedStationData?.partnerName ||
    fetchedStationData?.partner?.partnerName ||
    fetchedStationData?.partner?.name ||
    fetchedStationData?.cpoName ||
    '';

  /**
   * Fetch station details when station ID is present
   * @param {string} currentStationId - The ID of the station to fetch
   */
  const handleFetchStationInfo = async (currentStationId: string) => {
    try {
      const stationResponse =
        await stationPartnerApi.fetchChargingStationByStationId(currentStationId);
      const data = stationResponse?.data || stationResponse;
      if (data) {
        setFetchedStationData(data);
      }
    } catch {
      try {
        const fallbackResponse =
          await chargingLocationAPI.fetchChargingStationById(currentStationId);
        const data = fallbackResponse?.data || fallbackResponse;
        if (data) {
          setFetchedStationData(data);
        }
      } catch (error) {
        console.error('Error fetching station details:', error);
      }
    }
  };

  useEffect(() => {
    if (stationId) {
      handleFetchStationInfo(stationId);
    } else {
      setFetchedStationData(null);
    }
  }, [stationId]);

  /**
   * Calculates the status color of the device based on its connectors' statuses.
   * @returns {string} The hex color code representing the status.
   */
  const getDeviceColor = () => {
    const statuses = connectors.map((c) => c.status || '');

    if (statuses.some((status) => status === 'Available')) {
      return '#22c55e';
    }

    if (
      statuses.some(
        (status) => status === 'Charging' || status === 'Preparing' || status === 'Finishing',
      )
    ) {
      return '#3b82f6';
    }

    if (statuses.some((status) => status === 'Reserved')) {
      return '#FFC107';
    }

    return '#e41010';
  };

  /**
   * Refetch parent details to sync the UI with updated charging point/station information
   */
  const handleRefreshDeviceDetails = async () => {
    const chargingPointId =
      (selectedDeviceGeneral as any)?._id || (selectedDeviceGeneral as any)?.id;
    if (chargingPointId && setSelectedDeviceGeneral) {
      try {
        if (hasDeviceHub) {
          const detailResponse = await deviceHubApi.fetchAdvanceDeviceDetails(chargingPointId);
          const data = detailResponse?.data || detailResponse || {};

          const connectorsMapped = (data.connectorInformation || []).map(
            (conn: any, index: number) => {
              let ratingValue = '';
              if (conn.connectorRating) {
                ratingValue =
                  typeof conn.connectorRating === 'object'
                    ? conn.connectorRating.capacityKW
                    : conn.connectorRating;
              }
              return {
                ...conn,
                connectorId: conn.connectorId || index + 1,
                status: conn.status || 'unknown',
                connectorRating: ratingValue,
              };
            },
          );

          const stationNameValue =
            data.stationName ||
            data.chargingStationName ||
            data.chargingStation?.name ||
            data.chargingStation?.stationName ||
            data.chargingStation?.chargingStationName ||
            '';
          const partnerNameValue =
            data.partnerName ||
            data.accountName ||
            data.account?.accountName ||
            data.account?.name ||
            data.partner?.partnerName ||
            data.partner?.name ||
            data.chargingStation?.accountName ||
            data.chargingStation?.account?.accountName ||
            data.chargingStation?.account?.name ||
            data.chargingStation?.partnerName ||
            data.chargingStation?.partner?.name ||
            data.cpoName ||
            data.tenantName ||
            data.tenantId ||
            '';

          setSelectedDeviceGeneral({
            ...data,
            id: data._id || data.id,
            chargingPointName: data.serialNumber || '',
            chargingPointSn: data.serialNumber || '',
            chargePointVendor: data.brandName || '',
            chargePointModel: data.modelName || '',
            vendor: data.brandName || '',
            model: data.modelName || '',
            maxPowerAvailable: data.specifications?.powerOutput
              ? `${Number(data.specifications.powerOutput).toFixed(3)} kW`
              : '',
            maximumOutputPower: data.specifications?.powerOutput
              ? String(data.specifications.powerOutput)
              : '0',
            maximumOutputPowerUnit: 'kW',
            serialNumber: data.serialNumber || '',
            tenantId: data.tenantName || '',
            reachableState: data.status || '',
            positionType: data.installationType || '',
            currentType: data.currentType || '',
            connectors: connectorsMapped,
            stationName: stationNameValue,
            partnerName: partnerNameValue,
          });
        } else {
          const detailResponse = await deviceApi.fetchChargingPointGeneralDetails(chargingPointId);
          const enelData = detailResponse?.data?.enelData || {};
          const stationNameValue =
            detailResponse?.data?.stationName ||
            detailResponse?.data?.chargingStationName ||
            detailResponse?.data?.chargingStation?.name ||
            detailResponse?.data?.chargingStation?.stationName ||
            detailResponse?.data?.chargingStation?.chargingStationName ||
            '';
          const partnerNameValue =
            detailResponse?.data?.partnerName ||
            detailResponse?.data?.accountName ||
            detailResponse?.data?.account?.accountName ||
            detailResponse?.data?.account?.name ||
            detailResponse?.data?.partner?.partnerName ||
            detailResponse?.data?.partner?.name ||
            detailResponse?.data?.chargingStation?.accountName ||
            detailResponse?.data?.chargingStation?.account?.accountName ||
            detailResponse?.data?.chargingStation?.account?.name ||
            detailResponse?.data?.chargingStation?.partnerName ||
            detailResponse?.data?.chargingStation?.partner?.name ||
            detailResponse?.data?.cpoName ||
            detailResponse?.data?.tenantName ||
            detailResponse?.data?.tenantId ||
            '';
          setSelectedDeviceGeneral({
            ...detailResponse.data,
            stationName: stationNameValue,
            partnerName: partnerNameValue,
            maxPowerAvailable: detailResponse?.data?.maximumOutputPower
              ? `${Number(detailResponse.data.maximumOutputPower).toFixed(3)} ${detailResponse.data.maximumOutputPowerUnit || ''}`
              : '',
            serialNumber: enelData.sn !== undefined ? String(enelData.sn) : undefined,
            tenantId: enelData.tenantId !== undefined ? String(enelData.tenantId) : undefined,
            maxPowerPoc:
              enelData.powerMaxPoc !== undefined ? String(enelData.powerMaxPoc) : undefined,
            reachableState:
              enelData.isReachableState !== undefined
                ? String(enelData.isReachableState)
                : undefined,
            acVoltage:
              enelData.acElectricCurrent?.voltage !== undefined
                ? String(enelData.acElectricCurrent.voltage)
                : undefined,
            positionType:
              enelData.csTechnicalConfiguration?.address?.position?.type !== undefined
                ? String(enelData.csTechnicalConfiguration.address.position.type)
                : undefined,
            rvxg: enelData.isRVxG !== undefined ? String(enelData.isRVxG) : undefined,
            rvxgAlgorithm:
              enelData.rvxgAlgorithm !== undefined ? String(enelData.rvxgAlgorithm) : undefined,
            rvxgLoadAreaId:
              enelData.rvxgLoadAreaId !== undefined ? String(enelData.rvxgLoadAreaId) : undefined,
            rvxgLoadAreaName:
              enelData.rvxgLoadAreaName !== undefined
                ? String(enelData.rvxgLoadAreaName)
                : undefined,
          });
        }
      } catch (error) {
        console.error('Error refreshing device details:', error);
      }
    }
  };

  const [activeTabKey, setActiveTabKey] = useState('connectors');

  const containerTabs: AvailableTabProperties[] = [
    {
      label: t('connectors', 'Connectors'),
      key: 'connectors',
      isActive: activeTabKey === 'connectors',
    },
    ...(hasDeviceHub
      ? []
      : [
          {
            label: t('location', 'Location'),
            key: 'location',
            isActive: activeTabKey === 'location',
          },
        ]),
  ];

  // Station Panel State
  const [stationPanelOpen, setStationPanelOpen] = useState(false);
  const [isStationEditMode, setIsStationEditMode] = useState(false);
  const [mapPanelOpen, setMapPanelOpen] = useState(false);

  // Media States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedUrls, setRemovedUrls] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  /**
   * Handle image file upload selection and validation
   * @param {any} event_ - The file input change event
   */
  const handleFileChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    if (!event_.target.files) return;
    const filesArray = Array.from(event_.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSizeBytes = 1 * 1024 * 1024; // 1MB

    const existingMedias = selectedDeviceGeneral?.medias || [];
    const currentCount =
      existingMedias.filter((m) => !removedUrls.includes(m.url || '')).length + newFiles.length;
    const spaceLeft = 10 - currentCount;

    if (spaceLeft <= 0) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('maxImagesLimit', 'You can upload a maximum of 10 images.'),
          type: 'error',
        }),
      );
      return;
    }

    const validFiles: File[] = [];
    let hasInvalidType = false;
    let hasInvalidSize = false;

    filesArray.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        hasInvalidType = true;
      } else if (file.size > maxSizeBytes) {
        hasInvalidSize = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasInvalidType) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileType', 'Only JPG and PNG files are allowed.'),
          type: 'error',
        }),
      );
    }

    if (hasInvalidSize) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileSize', 'Files must be smaller than 1MB.'),
          type: 'error',
        }),
      );
    }

    if (validFiles.length > 0) {
      const filesToAdd = validFiles.slice(0, spaceLeft);
      setNewFiles((previous) => [...previous, ...filesToAdd]);
      if (validFiles.length > spaceLeft) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t(
              'maxImagesLimitIgnored',
              'Some files were ignored because you can upload a maximum of 10 images.',
            ),
            type: 'error',
          }),
        );
      }
    }

    // eslint-disable-next-line no-param-reassign
    event_.target.value = '';
  };

  /**
   * Remove an existing image URL from view and mark it for removal
   * @param {string} url - The URL of the image to remove
   */
  const handleRemoveExistingMedia = (url: string) => {
    setRemovedUrls((previous) => [...previous, url]);
  };

  /**
   * Remove a newly added image from the upload list
   * @param {number} index - The index of the file to remove
   */
  const handleRemoveNewFile = (index: number) => {
    setNewFiles((previous) => previous.filter((_, index_) => index_ !== index));
  };

  /**
   * Close the edit modal and discard unsaved selections
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setNewFiles([]);
    setRemovedUrls([]);
  };

  /**
   * Save the image additions and removals by invoking the API
   */
  const handleSaveMedia = async () => {
    const chargingPointId =
      (selectedDeviceGeneral as any)?._id || (selectedDeviceGeneral as any)?.id;
    if (!chargingPointId) return;

    // Check if any new file exceeds 1MB
    const maxSizeBytes = 1 * 1024 * 1024;
    const hasLargeFile = newFiles.some((file) => file.size > maxSizeBytes);
    if (hasLargeFile) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileSize', 'Files must be smaller than 1MB.'),
          type: 'error',
        }),
      );
      return;
    }

    setIsSaving(true);

    const formData = new FormData();
    newFiles.forEach((file) => {
      formData.append('medias', file);
    });
    removedUrls.forEach((url, index) => {
      formData.append(`removedUrls[${index}]`, url);
    });

    if (!hasDeviceHub) {
      const sn =
        selectedDeviceGeneral.chargingPointSn || (selectedDeviceGeneral as any).serialNumber || '';
      const model =
        (selectedDeviceGeneral as any).chargePointModel || selectedDeviceGeneral.model || '';
      const vendor =
        (selectedDeviceGeneral as any).chargePointVendor || selectedDeviceGeneral.vendor || '';
      const maxPower = selectedDeviceGeneral.maximumOutputPower
        ? String(selectedDeviceGeneral.maximumOutputPower)
        : '0';
      const maxPowerUnit = selectedDeviceGeneral.maximumOutputPowerUnit || 'kW';
      const currentT = (selectedDeviceGeneral as any).currentType || '';

      formData.append('chargingPointName', selectedDeviceGeneral.chargingPointName || '');
      formData.append('chargingPointSn', sn);
      formData.append('chargePointModel', model);
      formData.append('chargePointVendor', vendor);
      formData.append('maximumOutputPower', maxPower);
      formData.append('maximumOutputPowerUnit', maxPowerUnit);
      formData.append('currentType', currentT);

      // Append connectors
      const existingConnectors = selectedDeviceGeneral.connectors || [];
      existingConnectors.forEach((c: any, index: number) => {
        formData.append(`connectors[${index}][connectorId]`, String(c.connectorId));
        const connTypes = Array.isArray(c.connectorType)
          ? c.connectorType
          : [c.connectorType || ''];
        connTypes.forEach((type: string, typeIndex: number) => {
          formData.append(`connectors[${index}][connectorType][${typeIndex}]`, type);
        });
        formData.append(`connectors[${index}][connectorFormat]`, c.connectorFormat || 'cable');
        formData.append(`connectors[${index}][connectorRating]`, String(c.connectorRating || '0'));
        formData.append(`connectors[${index}][maxCurrent]`, String(c.maxCurrent || '0'));
        formData.append(`connectors[${index}][maxVoltage]`, String(c.maxVoltage || '0'));
        formData.append(`connectors[${index}][unit]`, c.unit || 'kW');
      });
    }

    try {
      if (hasDeviceHub) {
        await deviceHubApi.updateDevice(chargingPointId, formData, true);
      } else if (isChargerManagement) {
        await devicePartnerApi.editDevice(formData, chargingPointId, true);
      } else {
        await deviceApi.editChargingPoint(chargingPointId, formData, true);
      }

      dispatch(
        setNotificationMessage({
          status: true,
          message: t('imagesUpdatedSuccessfully', 'Device images updated successfully.'),
          type: 'success',
        }),
      );

      await handleRefreshDeviceDetails();
      handleCloseEditModal();
    } catch (error: any) {
      console.error('Failed to save device images:', error);
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            t('failedToUpdateImages', 'Failed to update device images.'),
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle tab switch
   * @param {string} key - Selected tab key
   */
  const handleSwitchTab = (key: string) => {
    setActiveTabKey(key);
  };

  return (
    <div className="device-connector-wrap" dir={i18n.dir()}>
      {/* Tabs */}
      <div className="device-connector-wrapper">
        <TabsContainer containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
      </div>

      {/* Content */}
      <div className="device-content">
        {(() => {
          const activeTab = containerTabs.find((tab) => tab.isActive);

          let dataToShow = null;

          if (activeTab) {
            switch (activeTab.key) {
              case 'connectors':
                dataToShow = (
                  <div className="device-connector-tab-container" style={{ width: '100%' }}>
                    <ConnectorSidePanel
                      connectors={connectors}
                      currentType={currentType}
                      showHeader={false}
                      showActions={showConnectorActions}
                      id={(selectedDeviceGeneral as any)?._id || (selectedDeviceGeneral as any)?.id}
                      chargingPointSn={selectedDeviceGeneral?.chargingPointSn}
                    />
                  </div>
                );
                break;

              case 'location':
                dataToShow = (
                  <div className="device-map-tab-wrap">
                    <div className="device-map-header">
                      <h4 className="device-map-title">{t('stationDetails')}</h4>
                    </div>

                    <div className="device-general-details-container">
                      <div className="each-general-detail">
                        <div className="general-detail-content">
                          <h1>
                            {stationId ? (
                              <button
                                type="button"
                                className="station-link-btn"
                                onClick={() => {
                                  setIsStationEditMode(false);
                                  setStationPanelOpen(true);
                                }}>
                                {stationName || '-'}
                              </button>
                            ) : (
                              stationName || '-'
                            )}
                          </h1>
                          <h6>{t('stationName', 'Station Name')}</h6>
                        </div>
                      </div>
                      <div className="each-general-detail">
                        <div className="general-detail-content">
                          <h1>{partnerName || '-'}</h1>
                          <h6>{t('partnerName', 'Partner name')}</h6>
                        </div>
                      </div>
                      <div className="each-general-detail map-view-card">
                        <div className="general-detail-content">
                          <DeviceMap height="100%" color={getDeviceColor()} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
                break;
              default:
                break;
            }
          }

          return dataToShow;
        })()}
      </div>

      {/* Side Panel for Edit Station */}
      <SidePaneModel
        open={stationPanelOpen}
        hideHeader
        content={
          <StationSidePanel
            fetchIdFrom="props"
            id={stationId}
            initialIsEditMode={isStationEditMode}
            onClose={() => setStationPanelOpen(false)}
            onSuccess={() => {
              setStationPanelOpen(false);
              handleRefreshDeviceDetails();
            }}
          />
        }
        closePanel={() => setStationPanelOpen(false)}
      />

      {/* Modal for Map */}
      <ModalBox
        open={mapPanelOpen}
        title={t('maps', 'Map')}
        header
        closeModal={() => setMapPanelOpen(false)}
        content={
          <div style={{ width: '60vw', minWidth: '300px', padding: '16px' }}>
            <DeviceMap height="60vh" />
          </div>
        }
      />

      {/* Modal for Image Preview */}
      <ModalBox
        open={!!selectedImage}
        title={t('imagePreview', 'Image Preview')}
        closeModal={() => setSelectedImage(null)}
        header
        position="center"
        content={
          <div
            className="image-preview-modal-content"
            style={{
              width: '75vw',
              minWidth: '320px',
              height: '70vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <img
              src={selectedImage || ''}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
        }
      />

      {/* Modal for Edit Device Images */}
      <ModalBox
        open={isEditModalOpen}
        title={t('deviceImagesTitle', 'Device Images')}
        closeModal={handleCloseEditModal}
        header
        position="center"
        content={
          <div className="station-images-modal-body">
            <p className="modal-subtitle">
              {t(
                'uploadMultipleImagesDesc',
                'Upload multiple images of your charging device. You can add up to 10 images.',
              )}
            </p>

            <div className="images-grid">
              {/* Existing Medias */}
              {(selectedDeviceGeneral?.medias || []).map((media, index) => {
                const url = media.signedUrl || media.url;
                const isRemoved = removedUrls.includes(media.url || '');
                if (isRemoved) return null;
                return (
                  <div className="modal-image-card" key={(media as any)._id || index}>
                    <div className="image-preview-wrapper">
                      <img
                        src={url}
                        alt=""
                        onError={(event_) => {
                          const img = event_.currentTarget as HTMLImageElement;
                          img.src = brandImage.chargingDevice;
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-overlay-btn"
                        onClick={() => handleRemoveExistingMedia(media.url || '')}>
                        <span>&times;</span>
                      </button>
                    </div>
                    <div className="image-filename-label">{getFileNameFromUrl(url || '')}</div>
                  </div>
                );
              })}

              {/* New files preview */}
              {newFiles.map((file, index) => {
                const objectUrl = URL.createObjectURL(file);
                return (
                  <div className="modal-image-card" key={index}>
                    <div className="image-preview-wrapper">
                      <img src={objectUrl} alt="" />
                      <button
                        type="button"
                        className="remove-image-overlay-btn"
                        onClick={() => handleRemoveNewFile(index)}>
                        <span>&times;</span>
                      </button>
                    </div>
                    <div className="image-filename-label">{file.name}</div>
                  </div>
                );
              })}

              {/* Add More dashed button */}
              {(selectedDeviceGeneral?.medias || []).filter(
                (m) => !removedUrls.includes(m.url || ''),
              ).length +
                newFiles.length <
                10 && (
                <label className="modal-add-more-box-btn" htmlFor="device-image-upload-input">
                  <input
                    id="device-image-upload-input"
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <div className="dashed-box-content">
                    <span className="plus-icon">+</span>
                    <span className="box-title">{t('addMoreImages', 'Add More Images')}</span>
                    <span className="box-desc">{t('fileRequirements', 'JPG, PNG up to 1MB')}</span>
                  </div>
                </label>
              )}
            </div>

            <div className="modal-instructions-footer">
              <span className="info-icon">â„¹</span>
              <span className="info-text">
                {t('dragDropReorderInfo', 'Drag and drop to reorder images')}
              </span>
            </div>

            <div className="modal-actions-container">
              <MyButton
                label={t('cancel')}
                buttonType="primary-outline"
                onClickFunc={handleCloseEditModal}
              />
              <MyButton
                label={isSaving ? t('saving', 'Saving...') : t('save')}
                buttonType="submit"
                onClickFunc={handleSaveMedia}
                disableButton={isSaving}
                loading={isSaving}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}

DeviceConnector.defaultProps = {
  setSelectedDeviceGeneral: undefined,
};

export default DeviceConnector;
